import React, { useMemo } from 'react';
import copyToClipboard from 'copy-to-clipboard';
import * as XLSX from 'xlsx';
import { calculateNEISBytes } from '../../utils/neisBytes';
import { downloadBlobFile } from '../../utils/downloadUtils';
import { useToast } from '../Toast';
import './SaenggibuResultActions.css';

const XLSX_MIME_TYPE = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const normalizeItem = (item) => {
  if (typeof item === 'string') {
    return { content: item, level: '', label: '', byteCount: null };
  }
  if (!item || typeof item !== 'object') return null;
  return {
    content: String(item.content || ''),
    level: String(item.level || '').trim(),
    label: String(item.label || '').trim(),
    byteCount: Number(item.byteCount ?? item.byte_count) || null,
  };
};

export const normalizeSaenggibuResultItems = (items = []) => (
  (Array.isArray(items) ? items : [])
    .map(normalizeItem)
    .filter((item) => item?.content.trim())
);

export const collectNormalSaenggibuResults = ({
  generationResult = '',
  versions = [],
  versionLevels = [],
  multiClassResults = [],
  resultCategory = '',
} = {}) => {
  if (Array.isArray(multiClassResults) && multiClassResults.length > 0) {
    return normalizeSaenggibuResultItems(
      multiClassResults.flatMap((classResult, classIndex) => (
        (classResult?.versions || []).map((content, versionIndex) => ({
          content,
          level: classResult?.levels?.[versionIndex] || '',
          label: `${classResult?.name || `${classIndex + 1}반`} 학생 ${versionIndex + 1}`,
        }))
      ))
    );
  }

  const nonEmptyVersions = (Array.isArray(versions) ? versions : [])
    .map((content, index) => ({
      content,
      level: versionLevels?.[index] || '',
      label: resultCategory === '교과' || resultCategory === '창체'
        ? `학생 ${index + 1}`
        : (versions.length > 1 ? `버전 ${index + 1}` : ''),
    }))
    .filter((item) => String(item.content || '').trim());

  if (nonEmptyVersions.length > 1) {
    return normalizeSaenggibuResultItems(nonEmptyVersions);
  }

  const singleContent = String(generationResult || nonEmptyVersions[0]?.content || '');
  return normalizeSaenggibuResultItems(singleContent.trim() ? [{
    content: singleContent,
    level: nonEmptyVersions[0]?.level || versionLevels?.[0] || '',
    label: nonEmptyVersions[0]?.label || '',
  }] : []);
};

export const buildSaenggibuCopyText = (items = []) => (
  normalizeSaenggibuResultItems(items)
    .map((item, index) => `${index + 1}. ${item.content}`)
    .join('\n\n')
);

export const buildSaenggibuExcelRows = (items = []) => {
  const normalized = normalizeSaenggibuResultItems(items);
  const hasLabels = normalized.some((item) => item.label);
  const hasLevels = normalized.some((item) => item.level);

  return normalized.map((item, index) => {
    const row = { 번호: index + 1 };
    if (hasLabels) row.구분 = item.label;
    if (hasLevels) row.수준 = item.level;
    row.내용 = item.content;
    row.바이트 = item.byteCount || calculateNEISBytes(item.content);
    return row;
  });
};

export const copySaenggibuText = async (text) => {
  if (!text) return false;
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch (_) {
    // 보안 정책으로 Clipboard API가 막히면 execCommand 기반 폴백을 사용한다.
  }
  try {
    return copyToClipboard(text, { format: 'text/plain' });
  } catch (_) {
    return false;
  }
};

const sanitizeFilePart = (value, fallback) => (
  String(value || fallback)
    .replace(/[\\/:*?"<>|]/g, '_')
    .replace(/\s+/g, '_')
    .slice(0, 80)
);

const sanitizeSheetName = (value) => (
  String(value || '생기부결과').replace(/[\\/?*[\]:]/g, '_').slice(0, 31)
);

export const createSaenggibuExcelFile = ({
  items,
  filenamePrefix = '생기부_결과',
  sheetName = '생기부결과',
}) => {
  const rows = buildSaenggibuExcelRows(items);
  if (rows.length === 0) return null;

  const sheet = XLSX.utils.json_to_sheet(rows);
  const book = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(book, sheet, sanitizeSheetName(sheetName));
  const bytes = XLSX.write(book, { bookType: 'xlsx', type: 'array' });
  const filename = `${sanitizeFilePart(filenamePrefix, '생기부_결과')}_${rows.length}개.xlsx`;
  const downloaded = downloadBlobFile(new Blob([bytes], { type: XLSX_MIME_TYPE }), filename);
  return downloaded ? filename : null;
};

const SaenggibuResultActions = ({
  items = [],
  filenamePrefix = '생기부_결과',
  sheetName = '생기부결과',
  ariaLabel = '생기부 결과 작업',
}) => {
  const { showToast } = useToast();
  const normalizedItems = useMemo(() => normalizeSaenggibuResultItems(items), [items]);

  if (normalizedItems.length === 0) return null;

  const handleCopyAll = async () => {
    const copied = await copySaenggibuText(buildSaenggibuCopyText(normalizedItems));
    showToast(
      copied ? '전체 문장을 복사했어요' : '복사하지 못했어요. 다시 시도해주세요',
      copied ? 'success' : 'error'
    );
  };

  const handleDownloadExcel = () => {
    try {
      const filename = createSaenggibuExcelFile({
        items: normalizedItems,
        filenamePrefix,
        sheetName,
      });
      showToast(
        filename ? `${filename}을 내려받았어요` : '내려받을 결과가 없어요',
        filename ? 'success' : 'warning'
      );
    } catch (error) {
      console.error('생기부 결과 엑셀 생성 실패:', error);
      showToast('엑셀 파일을 만들지 못했어요. 다시 시도해주세요', 'error');
    }
  };

  return (
    <div className="saenggibu-result-actions" role="group" aria-label={ariaLabel}>
      <button type="button" onClick={handleCopyAll}>전체 복사</button>
      <button type="button" onClick={handleDownloadExcel}>엑셀 받기</button>
    </div>
  );
};

export default SaenggibuResultActions;
